import { AfterViewChecked, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { EnvironmentCustomService } from './environment-custom/services/environment-custom.service';
import { EnvironmentService } from './services/environment.service';
import { ConfirmDialogComponent } from '../shared/components/confirm-dialog/confirm-dialog.component';
import { Subscription } from 'rxjs/Subscription';
import { BzgToastService } from 'bz-genome-angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { EnvironmentDataService } from './services/environment.data.service';
import { EnvironmentAdvancedComponent } from './environment-advanced/environment-advanced.component';
import { IEnvironment } from './models/environment';
import { EnvironmentPopularComponent } from './environment-popular/environment-popular.component';
import { SSL_OP_NO_TLSv1_1, SSL_OP_NO_TLSv1_2 } from 'constants';
import { IAppState } from 'app/IAppState';
import { IConfigurationEmail } from './typings/IConfigurationEmail';

@Component({
  selector: 'environment',
  templateUrl: 'environment.component.html',
  styleUrls: ['environment.component.scss']
})
export class EnvironmentComponent implements AfterViewChecked {
  isBusy = true;
  invalidFields: any = [];
  subscriptionPendingChanges: Subscription;
  saveAll: Subscription;
  public metadata: any;
  public environment: IEnvironment;

  public tabs: {};
  private _tabsSubscription: Subscription;
  public selectedTab: number = 0;

  public configurationEmail: IConfigurationEmail = null;
  public configurationEmailObject: any = null;
  public SMTPSenderAcc: string = null;
  public defaultSender: string = null;

  public EnableWsSecurity: boolean = false;

  @ViewChild('confirmDialog', { static: false }) confirmDialog: ConfirmDialogComponent;
  @ViewChild('environmentPopularComponent', { static: false }) environmentPopularComponent: EnvironmentPopularComponent;
  @ViewChild('environmentAdvancedComponent', { static: false }) environmentAdvancedComponent: EnvironmentAdvancedComponent;

  constructor(
    private _environmentCustomService: EnvironmentCustomService,
    private _environmentService: EnvironmentService,
    private _toastService: BzgToastService,
    private _translateService: TranslateService,
    private _store: Store<IAppState>,
    private _environmentDataService: EnvironmentDataService,
    private cdRef: ChangeDetectorRef
  ) {
    this.subscriptionPendingChanges = this._environmentService.showPendingChangesDialog$.subscribe(() => {
      this._environmentService.changeDialogResponse$ = this.confirmDialog.showDialog();
    });
    this.saveAll = this._environmentService.saveAll$.subscribe(() => {
      this.save();
    });
  };

  async ngOnInit() {
    this._initializeEnvironmentConfiguration();

    this._tabsSubscription = this._store
      .select((s) => s.menuOptions)
      .subscribe((menuOptions: any) => {
        this.tabs = menuOptions['Environment'];
      });

    this.metadata = await this._environmentDataService.getEnvironmentMetadata();
    this.getEmailConfiguration();
    this.getEmailDefaultWebConfig();
    this.getKeyWsSecurity();
  };

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    // prevent memory leak when component destroyed
    this.subscriptionPendingChanges.unsubscribe();
    this.saveAll.unsubscribe();
    this._tabsSubscription.unsubscribe();
  }

  getEmailConfiguration() {
    this._environmentDataService.getEmailConfiguration().subscribe(res => {
      if (res && res.content) {
        this.configurationEmailObject = res;
        this.configurationEmail = res.content;
      }
    });
  }

  getKeyWsSecurity() {
    this._environmentDataService.getWSSecurityKey().subscribe(EnableWsSecurity => {
      if (EnableWsSecurity)
        this.EnableWsSecurity = EnableWsSecurity;
    })
  }

  getEmailDefaultWebConfig() {
    this._environmentDataService.getEmailDefaultWebConfig().subscribe(res => {
      if (res && res.SMTPSenderAcc) {
        let atIndex = res.SMTPSenderAcc.indexOf('@');
        this.SMTPSenderAcc = res.SMTPSenderAcc.slice(atIndex);
        this.defaultSender = res.SMTPSenderAcc.slice(0, atIndex);
      }
    });
  }

  public async save() {
    const self = this;
    if (!(this.isFormValid())) {
      return;
    }

    const isAppsAnonymousUserEnable = this.tabs?.['Popular']?.['Sections']?.['AppsAnonymousUser'] ?? false;
    this.isBusy = true;
    this._environmentService.saveEnvironmentConfiguration(isAppsAnonymousUserEnable).subscribe(() => {
      this.saveEmail(self);
    });
  };

  isGuidOauth(guid) {
    let isGuid = false;
    if (guid) {
      if (guid.length === 36) {
        const regex = /\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b/g;
        const guidValid = guid.match(regex);
        if (guidValid)
          isGuid = true;
      }
    }
    return isGuid;
  }

  genericValidation(value, field) {
    let regex = null;
    if (value === 0)
      regex = /^[a-zA-Z0-9-.:/,]+$/;
    else if (value === 1)
      regex = /^([\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,})(\s*;\s*[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,})*$/;
    else if (value === 2)
      regex = /^[a-zA-Z0-9-_.#]+$/;
    else if (value == 3)
      regex = /^\S+@\S+\.\S+$/;
    else
      regex = /^\S+$/;

    let error = false;
    if (field) {
      const resultado = regex.test(field);
      if (!resultado) {
        error = true;
      }
    } else
      error = true;
    return error;
  }

  conditionsSender(prefixSender) {
    let error = false;
    if (prefixSender) {
      const firstCharacter = prefixSender[0];
      const lastCharacter = prefixSender[prefixSender.length - 1];
      const allowFirst = firstCharacter.match(/[-_.#]/);
      const allowLast = lastCharacter.match(/[-_.#]/);
      const doubleCharacter = prefixSender.match(/^(?!.*[-_.#]{2})[a-zA-Z0-9-_.#]+$/);

      if (allowFirst || allowLast || !doubleCharacter)
        error = true;
    }
    return error;
  }



  public saveEmail(self) {
    this.configurationEmailObject.content = self.configurationEmail;
    this._environmentService.saveEmailConfiguration(this.configurationEmailObject).subscribe(resp => {
      if (resp.changes) {
        self.isBusy = false;
        self._notifySuccessOperation();
      }
    });
  }

  public isFormValid(): boolean {
    let isValid = true;

    const isValidPopularEnvironment = this.environmentPopularComponent.isFormValid();
    const isValidAdvancedEnvironment = this.environmentAdvancedComponent.isFormValid();

    if (!isValidPopularEnvironment || !isValidAdvancedEnvironment || this.extraValidations()) {
      isValid = false;
      this._translateService.get('ENVIRONMENT.INVALID_FORM').subscribe((message: string) => {
        this._toastService.open(
          message,
          {
            type: 'error',
            duration: 0,
            verticalPosition: 'top',
            horizontalPosition: 'right'
          });
      });
    }

    return isValid;
  }

  extraValidations() {
    let active = this.configurationEmailObject.content.active;

    let prefixerSender = this.configurationEmailObject.content.methods[0].options.prefixSender;

    let smtpServerBasic = this.configurationEmailObject.content.methods[1].options.smtpServer;
    let sendEmailBCCTo = this.configurationEmailObject.content.methods[1].options.sendEmailBCCTo;
    let sendEmailCCTo = this.configurationEmailObject.content.methods[1].options.sendEmailCCTo;
    let smtpSenderAcc = this.configurationEmailObject.content.methods[1].options.smtpSenderAcc;
    let errorSendEmailBCCTo = sendEmailBCCTo.length > 0 && this.genericValidation(1, sendEmailBCCTo);
    let errorSendEmailCCTo = sendEmailCCTo.length > 0 && this.genericValidation(1, sendEmailCCTo);

    let guidOAuth = this.configurationEmailObject.content.methods[2].options.clientId;
    let smtpServer = this.configurationEmailObject.content.methods[2].options.smtpServer;
    let accessTokenURI = this.configurationEmailObject.content.methods[2].options.accessTokenURI;
    let sendEmailBCCToOAuth = this.configurationEmailObject.content.methods[2].options.sendEmailBCCTo;
    let sendEmailCCToOAuth = this.configurationEmailObject.content.methods[2].options.sendEmailCCTo;
    let errorSendEmailBCCToOAuth = sendEmailBCCToOAuth.length > 0 && this.genericValidation(1, sendEmailBCCToOAuth);
    let errorSendEmailCCToOAuth = sendEmailCCToOAuth.length > 0 && this.genericValidation(1, sendEmailCCToOAuth);
    let valiateWsSecurityEnable = this.EnableWsSecurity && this.environment.parameters.wsSecurityEnabled;
    let validateWsSecurityUserName = !!this.environment.parameters.wsSecurityUserName;
    let validateWsSecurityPassword = !!this.environment.parameters.wsSecurityPassword;

    let error = false;
    if (active === 'Default' && (prefixerSender.length > 0 && (this.conditionsSender(prefixerSender) || this.genericValidation(2, prefixerSender))))
      error = true;
    if (active === 'OAuth' && (!this.isGuidOauth(guidOAuth) || this.genericValidation(0, smtpServer)
      || this.genericValidation(4, accessTokenURI) || errorSendEmailBCCToOAuth || errorSendEmailCCToOAuth))
      error = true;
    if (active === 'Basic' && (this.genericValidation(0, smtpServerBasic) || errorSendEmailBCCTo ||
      errorSendEmailCCTo || this.genericValidation(1, smtpSenderAcc)))
      error = true;
    if (valiateWsSecurityEnable && (!validateWsSecurityUserName || !validateWsSecurityPassword))
      error = true;
    return error;
  }

  private _initializeEnvironmentConfiguration() {
    const self = this;
    this._environmentService.getEnvironmentConfiguration().subscribe((environment) => {
      self.isBusy = false;
      this.environment = environment;
    });
  };

  private _notifySuccessOperation() {
    this._translateService.get('ENVIRONMENT.COMPLETED_SUCCESSFULLY').subscribe((message: string) => {
      this._toastService.open(
        message,
        {
          type: 'success',
          duration: 5000,
          verticalPosition: 'top',
          horizontalPosition: 'right'
        });
    });

  };

}
